import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ResourceListPane } from '@smartsoftware/reflex-core';
import { MatSort } from '@angular/material/sort';

import { 
    SuccessAccount, 
    SuccessAccountFirm_Service,
    SystemConfig_service, 
    UserPermission_Service ,
    PagedResourceListComponent,
    SuccessAccountDataSource,
    UserDevicePreferencesService
} from 'legalreflex-lib';
import { BreadCrumbService } from '../../../components/breadcrumb-bar/breadcrumb-service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { FormBuilder } from '@angular/forms';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';

@Component({
    selector: 'success-account-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss']
})
export class SuccessAccountList extends PagedResourceListComponent<SuccessAccount> {

    @ViewChild(MatSort) sort: MatSort;
    @ViewChild('filter') filterPane: ResourceListPane = new ResourceListPane();
    public filterCollapsed: boolean = sessionStorage.getItem("filterCollapsed") == 'true';
    public isLoading: boolean = true;
    public isSynced: boolean = false;
    public userCorp: string = '';
    public userBranch: string = '';
    public userCompany: number = 0;
    public userCustomer: number = 0;
    public showCompany: boolean = false;
    public showCustomer: boolean = false;
    public columns: string[] = [
       'View', 'username', 'email', 'firstName', 'lastName', 'companyNumber', 'customerNumber'
    ];

    private destroy$: Subject<void> = new Subject<void>();

    private userSync$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        public dataSource: SuccessAccountDataSource,
        protected userPermissionService: UserPermission_Service,
        protected resourceService: SuccessAccountFirm_Service,
        protected userDevicePreferences: UserDevicePreferencesService,
        protected route: ActivatedRoute,
        protected router: Router,
        protected dialog: MatDialog,
        protected fb: FormBuilder,
        private breadCrumbService: BreadCrumbService,
        public sysConfigService: SystemConfig_service,
        protected auth: OidcSecurityService,
    ) {
        super(dataSource, resourceService, dialog, route, router);
        this.sort = new MatSort()
        this.setBreadCrumbs();

        let obj = {
            firstName: [''],
            lastName: [''],
            email: [''],
            username: [''],
            corpId: [''],
            customerNumber: [''],
            companyNumber: [''],
            deletedAt: [''],
            primaryClientBranch_uuid: ['']
        }
        this.filterFormGroup = this.fb.group(obj)
        this.filterFormGroup!.patchValue(this.userDevicePreferences.fcFilter);
        Object.keys(this.dataSource.filters).forEach((key) => (this.dataSource.filters[key] = this.userDevicePreferences.fcFilter[key]));
    }

    setBreadCrumbs(){
        this.breadCrumbService.breadcrumbs.next([
            { label: 'Users', url: '/users'}
        ]);
        let actions: any[] = [{ label: 'Refresh', action:this.filterSubmit.bind(this), icon:'refresh'}]
        if(this.showCompany && this.showCustomer){
            actions.push({ label: 'New User', routerLink: ['/users/new'], icon:'library_add'})
        }
        this.breadCrumbService.actions.next(actions);
    }

    pageChange(event?:any){
        this.selection.clear();
        this.setBreadCrumbs();
    }

    sortData(event: any){
        if(this.dataSource.sort){
            this.dataSource.sort.active = event.active
            this.dataSource.sort.direction = event.direction            
        }
        this.dataSource.reload();
    }

    ngOnInit(): void {
        super.ngOnInit();
        if(this.sysConfigService.maintenanceRedirectCheck()){  
            window.localStorage.removeItem("loggedInUserPermissionNodes");
            this.auth.logoff();
        };
        setTimeout(()=>{
            this.auth.isAuthenticated$
                .pipe(
                    filter(auth => !!auth),
                    switchMap(() => this.auth.userData$),
                    filter(ls => !!ls.userData.profile),
                    takeUntil(this.destroy$)
                )
                .subscribe((user) => {
                    this.userCorp = user.userData.profile.corpId
                    this.userBranch = user.userData.profile.primaryClientBranch_uuid
                    this.userCustomer = user.userData.profile.customerNumber
                    this.userCompany = user.userData.profile.companyNumber
                    
                    let filters = {
                        firstName: [''],
                        lastName: [''],
                        email: [''],
                        username: [''],
                        corpId: [this.userCorp],
                        customerNumber: [''],
                        companyNumber: [''],
                        deletedAt: [null],
                        primaryClientBranch_uuid: [''] 
                    }

                    if(user.userData.profile.uuid && user.userData.profile.internalUser){
                        this.showCompany = true;
                        this.showCustomer = true;

                        this.filterFormGroup = this.fb.group(filters)
                    }
                    else if(user.userData.profile.corpId) {

                        if(!this.showCompany){
                            Object.assign(filters, {companyNumber:[this.userCompany]})
                        } else if(!this.showCustomer){
                            Object.assign(filters, {customerNumber:[this.userCustomer]})
                        } 

                        this.filterFormGroup = this.fb.group(filters)
                    };
                });
        });

        this.userSync$
			.pipe(
				filter((sync) => !!sync),
				switchMap(() => this.userPermissionService.userSync),
				filter((auth) => !!auth),
				takeUntil(this.destroy$)
			)
			.subscribe((us) => {
				console.debug("user sync issue:", us);
				this.isSynced = true;

				this.showCompany =
					this.userPermissionService.loggedInUserPermissionNodes
						.MANAGEFIRM != undefined;
				this.showCustomer =
					this.userPermissionService.loggedInUserPermissionNodes
						.MANAGEOFFICE != undefined;
				if (this.showCompany && !this.userDevicePreferences.fcFilter.companyNumber)
					this.filterFormGroup?.get("companyNumber")?.setValue("");
				if (this.showCustomer && !this.userDevicePreferences.fcFilter.customerNumber)
					this.filterFormGroup?.get("customerNumber")?.setValue("");
				this.setBreadCrumbs();
				this.onFilterSubmit();
			});
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit()
        setTimeout(()=>{

            if (this.dataSource.sort) {
                this.dataSource.sort.active = 'username';
                this.dataSource.sort.direction = 'asc';
            }

            this.auth.isAuthenticated$
				.pipe(
					filter((auth) => !!auth),
					takeUntil(this.destroy$)
				)
				.subscribe(() => {
					let filterClick = document.getElementById("filterPaneUser");
					if (
						filterClick &&
						!filterClick.hasAttribute("filterClickAttached")
					) {
						this.filterPaneClick(filterClick, this.filterPane);
					}
				});

                let filters = {
					firstName: [""],
					lastName: [""],
					email: [""],
					username: [""],
					corpId: [this.userCorp],
					customerNumber: [""],
					companyNumber: [""],
					deletedAt: [null],
					primaryClientBranch_uuid: [""],
				};
                this.filterFormGroup = this.fb.group(filters);
                this.filterFormGroup!.patchValue(this.userDevicePreferences.fcFilter);
                this.filterFormGroup!.get("deletedAt")!.setValue(null);

                this.filterFormGroup!.valueChanges.pipe(
                    takeUntil(this.destroy$)
                ).subscribe(
                    (jsonObject) => {
                        this.userDevicePreferences.fcFilter = jsonObject;
                    }
                );
            this.filterSubmit();
        })

        this.dataSource.loading
			.pipe(takeUntil(this.destroy$))
			.subscribe((state) => {
				this.isLoading = state;
                document.getElementById("filterPaneUser")!.click();
			});
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.destroy$.next();
        this.destroy$.complete();
    }

    filterSubmit(reset?: boolean){
        if(!this.isSynced){
            this.userSync$.next(true);
        }else{
            if(reset){
                this.filterFormGroup?.patchValue({
                    firstName: '',
                    lastName: '',
                    email: '',
                    username: '',
                    corpId: this.userCorp,
                    customerNumber: !this.showCustomer ? this.userCustomer : '',
                    companyNumber: !this.showCompany ? this.userCompany : '',
                    deletedAt: null,
                    primaryClientBranch_uuid: !this.showCustomer ? this.userBranch : ''
                });
            }
        
            this.setBreadCrumbs();
            this.onFilterSubmit();
        }
    }

    override onFilterSubmit(): void {
        for (const control in this.filterFormGroup!.controls) {
            this.dataSource.filters[control] = this.filterFormGroup!.controls[control].value;
            if (!this.dataSource.filters[control] && control !== "deletedAt") delete this.dataSource.filters[control];
        }
        this.dataSource.reload();
    }
}